export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

export const NAV_LINKS = [
  { label: "Loan Programs", path: "/loan-programs" },
  { label: "EMD Funding", path: "/emd-funding" },
  { label: "Transactional Funding", path: "/transactional-funding" },
  { label: "Partners", path: "/partners" },
  { label: "Referral Program", path: "/referral-program" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export const FINANCING_TYPES = [
  { value: "fix-and-flip", label: "Fix & Flip", kind: "loan" },
  { value: "dscr-rental", label: "DSCR Rental", kind: "loan" },
  { value: "bridge", label: "Bridge", kind: "loan" },
  { value: "ground-up-construction", label: "Ground-Up Construction", kind: "loan" },
  { value: "multifamily", label: "Multifamily", kind: "loan" },
  { value: "portfolio", label: "Portfolio", kind: "loan" },
  { value: "emd-funding", label: "EMD Funding", kind: "emd" },
  { value: "transactional-funding", label: "Transactional Funding", kind: "transactional" },
];

export const DOC_CATEGORIES = [
  "Purchase Agreement","Assignment Agreement","Scope of Work","Bank Statements","Entity Documents",
  "Operating Agreement","Articles of Organization","Rent Roll","Lease","Property Photos","Appraisal",
  "Insurance","Title","Settlement Statement","Proof of Funds","Photo ID","EIN Letter","Track Record","Other",
];

export const CREDIT_RANGES = ["Below 620","620–659","660–699","700–739","740+","Not sure"];

// Product grid — descriptive copy only, NO hardcoded rates/terms (published only by admin).
export const PROGRAMS = {
  "fix-and-flip": {
    title: "Fix & Flip",
    nav: "Fix & Flip",
    headline: "Finance the acquisition. Fund the renovation.",
    short: "Financing for investment properties that require renovation, repositioning, or resale.",
    description:
      "Financing for investment properties that require renovation, repositioning, or resale. Structured around the deal so you can acquire, renovate, and exit with confidence.",
    image: "https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    whoFor: ["Active fix & flip investors","Value-add operators","Auction & distressed buyers","Investors scaling their volume"],
    useCases: ["Property acquisition","Light rehab","Heavy rehab","Value-add projects","Distressed properties","Auction purchases"],
    evaluate: ["Property & location","As-is and after-repair value","Scope and budget of work","Borrower experience","Exit strategy","Entity and liquidity"],
    documents: ["Purchase Agreement","Scope of Work","Bank Statements","Entity Documents"],
    cta: "APPLY FOR FINANCING",
    ctaType: "fix-and-flip",
  },
  "dscr-rental": {
    title: "DSCR Rental",
    nav: "DSCR Rental",
    headline: "Rental financing built around property performance.",
    short: "Debt Service Coverage Ratio (DSCR) financing underwritten on the property's income.",
    description:
      "Debt Service Coverage Ratio (DSCR) financing is underwritten around the property's income rather than personal income documentation. Built for investors growing a rental portfolio.",
    image: "https://images.unsplash.com/photo-1532888277436-2286814f0ee1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    whoFor: ["Buy-and-hold investors","Portfolio builders","BRRRR investors","Short-term rental operators where eligible"],
    useCases: ["Rental acquisition","Rate-and-term refinance","Cash-out refinance","Portfolio growth","Long-term rental","Short-term rental where eligible"],
    evaluate: ["Property income & DSCR","Market rents","Property condition","Borrower entity","Credit profile"],
    documents: ["Lease","Rent Roll","Entity Documents","Insurance"],
    cta: "SUBMIT A RENTAL DEAL",
    ctaType: "dscr-rental",
    calculator: true,
  },
  bridge: {
    title: "Bridge",
    nav: "Bridge",
    headline: "Capital when timing matters.",
    short: "Short-term capital for fast, transitional, or time-sensitive acquisitions.",
    description:
      "Short-term capital for transitional properties and time-sensitive acquisitions where speed and flexibility matter most.",
    image: "https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    whoFor: ["Investors on a tight timeline","Operators stabilizing an asset","Buyers awaiting permanent financing"],
    useCases: ["Fast acquisitions","Transitional properties","Stabilization","Distressed assets","Delayed permanent financing","Short-term refinance"],
    evaluate: ["As-is value","Exit / takeout strategy","Timeline","Property condition","Borrower experience"],
    documents: ["Purchase Agreement","Bank Statements","Entity Documents"],
    cta: "SUBMIT A BRIDGE DEAL",
    ctaType: "bridge",
  },
  "ground-up-construction": {
    title: "Ground-Up Construction",
    nav: "Ground-Up",
    headline: "Capital from dirt to completion.",
    short: "Construction financing for spec homes, build-to-rent, and residential development.",
    description:
      "Construction financing from land through completion for spec homes, build-to-rent, infill, and small-scale residential development.",
    image: "https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    whoFor: ["Builders & developers","Build-to-rent operators","Infill & spec builders"],
    useCases: ["Spec homes","Build-to-rent","Infill projects","Residential development","Small-scale construction"],
    evaluate: ["Land value","Purchase price","Construction budget","Completed value","Experience","Timeline"],
    documents: ["Scope of Work","Entity Documents","Bank Statements"],
    cta: "SUBMIT A CONSTRUCTION DEAL",
    ctaType: "ground-up-construction",
    extraFields: [
      { name: "land_value", label: "Land Value" },
      { name: "construction_budget", label: "Construction Budget" },
      { name: "completed_value", label: "Completed Value" },
      { name: "experience", label: "Construction Experience" },
      { name: "timeline", label: "Project Timeline" },
    ],
  },
  multifamily: {
    title: "Multifamily",
    nav: "Multifamily",
    headline: "Financing for larger investment properties.",
    short: "Financing for 5+ unit properties — acquisition, value-add, bridge, and refinance.",
    description:
      "Financing for larger investment properties including 5+ unit apartments, value-add, stabilization, and refinance.",
    image: "https://images.unsplash.com/photo-1532888277436-2286814f0ee1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    whoFor: ["Multifamily investors","Value-add operators","Syndicators & groups"],
    useCases: ["5+ unit properties","Acquisition","Value-add","Bridge","Stabilization","Refinance"],
    evaluate: ["Net operating income","Occupancy & rent roll","Property condition","Business plan","Sponsor experience"],
    documents: ["Rent Roll","Operating Agreement","Entity Documents","Bank Statements"],
    cta: "SUBMIT A MULTIFAMILY DEAL",
    ctaType: "multifamily",
  },
  portfolio: {
    title: "Portfolio Loans",
    nav: "Portfolio",
    headline: "Financing for multiple properties under one strategy.",
    short: "Consolidate or grow multiple rental properties under a single financing strategy.",
    description:
      "Financing designed for multiple properties under one strategy — from portfolio acquisitions to consolidation and cash-out.",
    image: "https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    whoFor: ["Portfolio owners","Investors consolidating debt","Investors scaling holdings"],
    useCases: ["Rental portfolios","Portfolio acquisitions","Portfolio refinance","Cash-out","Consolidation"],
    evaluate: ["Portfolio performance","Rent roll & occupancy","Property conditions","Entity structure","Borrower liquidity"],
    documents: ["Rent Roll","Portfolio Schedule (spreadsheet)","Entity Documents","Bank Statements"],
    cta: "SUBMIT MY PORTFOLIO",
    ctaType: "portfolio",
    portfolioUpload: true,
  },
};

export const PROGRAM_ORDER = ["fix-and-flip","dscr-rental","bridge","ground-up-construction","multifamily","portfolio"];

// Combo card media: property photo + Archer-in-role cutout (transparent PNGs in /public)
export const PROGRAM_MEDIA = {
  "fix-and-flip": { photo: "https://images.unsplash.com/photo-1517581177682-a085bb7ffb15?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", archer: "/archer_flipper.png" },
  "dscr-rental": { photo: "https://images.unsplash.com/photo-1628744448839-a475cc0e90c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", archer: "/archer_landlord.png" },
  bridge: { photo: "https://images.unsplash.com/photo-1757356657991-c3fd6e2e812e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", archer: "/archer_bridge.png" },
  "ground-up-construction": { photo: "https://images.pexels.com/photos/39151698/pexels-photo-39151698.jpeg?auto=compress&cs=tinysrgb&w=1200", archer: "/archer_construction.png" },
  multifamily: { photo: "https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", archer: "/archer_multifamily.png" },
  portfolio: { photo: "https://images.pexels.com/photos/3845512/pexels-photo-3845512.jpeg?auto=compress&cs=tinysrgb&w=1200", archer: "/archer_portfolio.png", flip: true },
  "emd-funding": { photo: "https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", archer: "/archer_emd.png" },
  "transactional-funding": { photo: "https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", archer: "/archer_transactional.png", flip: true },
};

export const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("fund")) return "text-ok border-ok/40 bg-ok/10";
  if (s.includes("declin")) return "text-brandred border-brandred/40 bg-brandred/10";
  if (s.includes("review") || s.includes("pending") || s.includes("information")) return "text-warn border-warn/40 bg-warn/10";
  if (s.includes("approved") || s.includes("verified") || s.includes("terms") || s.includes("closed") || s.includes("repaid")) return "text-ok border-ok/40 bg-ok/10";
  return "text-info border-info/40 bg-info/10";
};
